import { action, computed, makeObservable, observable } from 'mobx'

import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  SAVE_PASSWORD_SUCCESS,
  SET_PASSWORD,
} from '../../stores/EventStore/eventConstants'
import EventsStore from '../../stores/EventStore/Events.store'
import isWeakPassword from '../../utils/isWeakPassword'

export default class SavePasswordStore {
  @observable public isSaving: boolean = false
  @observable public password: string = null
  @observable public confirmationPassword: string = null
  @observable private passwordErrorMessage: string = null

  public constructor(private readonly eventsStore: EventsStore) {
    makeObservable(this)
  }

  @computed
  public get passwordChangeError(): string {
    if (this.passwordErrorMessage) {
      return this.passwordErrorMessage
    }

    if (!this.password) {
      return Localization.translator.passwordErrors.empty
    }

    if (isWeakPassword(this.password)) {
      return Localization.translator.passwordErrors.weakPassword
    }

    if (this.password !== this.confirmationPassword) {
      return Localization.translator.passwordErrors.doNotMatch
    }

    return null
  }

  // Writes through SET_PASSWORD (auth's /set-password), as Profile's form does.
  @action.bound
  public handleSetPassword() {
    // isSaving, not just validity: the button disables itself but Enter does
    // not, so without this a held key fires concurrent POSTs.
    if (this.passwordChangeError || this.isSaving) {
      return
    }

    this.isSaving = true
    this.eventsStore.dispatch(
      SET_PASSWORD,
      this.password,
      this.onSaved,
      this.onSaveFailed,
    )
  }

  @action.bound
  public resetError() {
    this.passwordErrorMessage = null
  }

  @action.bound
  public clear() {
    this.password = ''
    this.confirmationPassword = ''
    this.resetError()
  }

  @action.bound
  private onSaved() {
    this.isSaving = false
    // This store outlives the dialog — it is mounted in the app shell — so a
    // saved password would otherwise still be sitting in the fields if the
    // prompt were ever shown again.
    this.clear()
    this.eventsStore.dispatch(SAVE_PASSWORD_SUCCESS)
  }

  @action.bound
  private onSaveFailed(error?: Error) {
    this.isSaving = false
    this.passwordErrorMessage =
      error?.message || Localization.translator.somethingWentWrongPleaseTryAgain
  }
}
